import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { DISCLAIMER_TEXT } from "@/lib/constants";
import { Locate, MapPin, Loader2, X } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function BecomeVendor() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    business_name: "", category: "", city: "", description: "",
    phone: "", whatsapp: "", address: "", website: "",
    working_hours: "Mon-Sat 10:00-20:00", service_area: "",
    logo: "", banner: "",
    lat: null, lng: null,
  });
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);

  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("bv.geo_unsupported"));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((s) => ({
          ...s,
          lat: +pos.coords.latitude.toFixed(6),
          lng: +pos.coords.longitude.toFixed(6),
        }));
        toast.success(t("bv.location_captured"));
        setLocating(false);
      },
      (err) => {
        toast.error(err.code === 1 ? t("bv.geo_denied") : t("bv.geo_failed"));
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
    );
  };

  const clearLocation = () => setForm((s) => ({ ...s, lat: null, lng: null }));

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    // If user already has a vendor doc, fast-redirect to dashboard.
    // 404 (= no vendor doc yet) is the expected case here — silence it.
    api.get("/vendors/mine/profile")
      .then((r) => {
        if (r.data) navigate("/dashboard/vendor", { replace: true });
      })
      .catch(() => { /* expected for first-time vendors */ });
  }, [navigate]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/vendors", form);
      toast.success(t("bv.created_toast"));
      navigate("/dashboard/vendor");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("bv.create_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{t("bv.eyebrow")}</div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter">{t("bv.title")}</h1>
        <p className="text-muted-foreground mt-2">{t("bv.subtitle")}</p>
      </div>

      <Card className="p-6 border-border/60">
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.business_name")}</label>
            <Input required data-testid="vendor-form-business-name" value={form.business_name} onChange={set("business_name")} className="mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.category")}</label>
            <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
              <SelectTrigger className="mt-1" data-testid="vendor-form-category"><SelectValue placeholder={t("bv.choose_category")} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {cats.map((c) => (<SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.city")}</label>
            <Select value={form.city} onValueChange={(v) => setForm((s) => ({ ...s, city: v }))}>
              <SelectTrigger className="mt-1" data-testid="vendor-form-city"><SelectValue placeholder={t("bv.choose_city")} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {cities.map((c) => (<SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.description")}</label>
            <Textarea data-testid="vendor-form-description" value={form.description} onChange={set("description")} className="mt-1" rows={4} placeholder={t("bv.description_placeholder")} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.phone")}</label>
            <Input data-testid="vendor-form-phone" value={form.phone} onChange={set("phone")} className="mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.whatsapp")}</label>
            <Input data-testid="vendor-form-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} className="mt-1" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.address")}</label>
            <Input data-testid="vendor-form-address" value={form.address} onChange={set("address")} className="mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.website")}</label>
            <Input data-testid="vendor-form-website" value={form.website} onChange={set("website")} className="mt-1" placeholder="https://" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.hours")}</label>
            <Input data-testid="vendor-form-hours" value={form.working_hours} onChange={set("working_hours")} className="mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.service_area")}</label>
            <Input data-testid="vendor-form-service-area" value={form.service_area} onChange={set("service_area")} className="mt-1" placeholder={t("bv.service_area_placeholder")} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.logo_url")}</label>
            <Input data-testid="vendor-form-logo" value={form.logo} onChange={set("logo")} className="mt-1" placeholder="https://" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bv.banner_url")}</label>
            <Input data-testid="vendor-form-banner" value={form.banner} onChange={set("banner")} className="mt-1" placeholder="https://" />
          </div>

          <div className="sm:col-span-2 rounded-xl border border-border/60 p-4 space-y-2" data-testid="vendor-form-location-block">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" /> {t("bv.location_title")}
                </div>
                <p className="text-xs text-muted-foreground mt-1 max-w-md">
                  {t("bv.location_text").replace("{city}", form.city || "city")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {form.lat != null && form.lng != null && (
                  <span className="text-xs font-mono rounded-md bg-muted px-2 py-1" data-testid="vendor-form-coords">
                    {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
                  </span>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={captureLocation}
                  disabled={locating}
                  data-testid="vendor-form-capture-location"
                >
                  {locating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Locate className="h-3.5 w-3.5 mr-1" />}
                  {form.lat != null ? t("bv.recapture") : t("bv.use_my_location")}
                </Button>
                {form.lat != null && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="rounded-full"
                    onClick={clearLocation}
                    data-testid="vendor-form-clear-location"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="sm:col-span-2 text-sm sm:text-base text-muted-foreground p-4 rounded-xl bg-muted/60 border border-border/60 leading-relaxed">
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{t("bv.disclaimer")}</div>
            {DISCLAIMER_TEXT}
          </div>

          <div className="sm:col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="vendor-form-cancel">{t("bv.cancel")}</Button>
            <Button type="submit" disabled={busy || !form.business_name || !form.category || !form.city} className="rounded-full" data-testid="vendor-form-submit">
              {busy ? t("bv.saving") : t("bv.submit")}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
